// src/pages/HeathrowTaxi.jsx
import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import PostcodeQuotenew from "./PostcodeQuotenew";
import LiveStrip from "../components/LiveStrip";
import FloatingSupportChat from "../components/FloatingSupportChat";
import PremiumContactStrip from "../components/PremiumContactStrip";
import CcgPremiumReviews from "../components/CcgPremiumReviews";
import CcgRouteLhrStatusCompact from "../components/CcgRouteLhrStatusCompact";
import { FaPlaneDeparture, FaClock, FaRoute, FaPhoneAlt } from "react-icons/fa";
import "./Home.css";
import "./CcgRoutePremium.css";

const HeathrowTaxi = () => {
  const scrollToForm = () => {
    const el = document.getElementById("heathrow-quote-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

const seoTitle = "Heathrow Taxi & Fixed-Price Transfers | CheapCabGrab";
const seoDescription = "Book a fixed-price Heathrow airport taxi to & from Slough, Windsor, Maidenhead and London. Licensed drivers, 24/7 availability, instant online quotes, no surge pricing.";
const seoCanonical = "https://www.cheapcabgrab.com/heathrow-taxi";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "TaxiService",
  "name": "CheapCabGrab Heathrow Taxi Service",
  "description": "Fixed-price Heathrow airport taxi to and from Slough, Windsor, Maidenhead and London. Licensed drivers, meet & greet and 24/7 booking.",
  "url": "https://www.cheapcabgrab.com/heathrow-taxi",
  "brand": "CheapCabGrab",
  "provider": {
    "@type": "Organization",
    "name": "CheapCabGrab Ltd",
    "url": "https://www.cheapcabgrab.com"
  },
  "serviceType": "Airport transfer and taxi service",
  "serviceArea": {
    "@type": "Place",
    "name": "Heathrow Airport (LHR)"
  },
  "areaServed": [
    "Heathrow Airport",
    "Slough",
    "Windsor",
    "Maidenhead",
    "London"
  ],
  "availableChannel": {
    "@type": "ServiceChannel",
    "serviceUrl": "https://www.cheapcabgrab.com"
  },
  "priceRange": "££",
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Heathrow Taxi Fleet",
    "itemListElement": [
      {
        "@type": "Offer",
        "name": "Saloon Heathrow Taxi"
      },
      {
        "@type": "Offer",
        "name": "Estate / MPV Heathrow Taxi"
      },
      {
        "@type": "Offer",
        "name": "8-Seater Heathrow Minibus"
      },
      {
        "@type": "Offer",
        "name": "14-Seater Heathrow Minibus / Coach"
      }
    ]
  }
};

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Where do I meet my driver at Heathrow?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":
          "Your driver will usually meet you in the arrivals hall at your terminal with a nameboard. Kerbside pickup is also available — your exact meeting point is confirmed in your booking confirmation."
      }
    },
    {
      "@type": "Question",
      "name": "Do you track my flight?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":
          "Yes. We track your flight number for most airlines and automatically adjust pickup time if your flight is delayed. Standard free waiting time is included in every Heathrow booking."
      }
    },
    {
      "@type": "Question",
      "name": "Are parking and Heathrow drop-off charges included?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":
          "Heathrow’s terminal drop-off fees and short-stay parking charges may be added to your fare. You always see a clear breakdown before confirming your booking — no hidden fees."
      }
    },
    {
      "@type": "Question",
      "name": "Can I request a child seat for my Heathrow taxi?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":
          "Yes. Child seats can be requested in advance for Heathrow journeys. Add details in the booking notes and we will allocate the appropriate vehicle and seat type."
      }
    }
  ]
};

useEffect(() => {
  const setMeta = (selector, attrs) => {
    let el = document.querySelector(selector);
    if (!el) {
      el = document.createElement("meta");
      document.head.appendChild(el);
    }
    Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, value));
  };

  const setJsonLd = (id, data) => {
    let script = document.querySelector(`script[data-heathrow-taxi-jsonld="${id}"]`);
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-heathrow-taxi-jsonld", id);
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
  };

  const applySeo = () => {
    document.title = seoTitle;

    let canonicalEl = document.querySelector('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute("href", seoCanonical);

    setMeta('meta[name="description"]', { name: "description", content: seoDescription });
    setMeta('meta[name="keywords"]', {
      name: "keywords",
      content: "Heathrow taxi, Heathrow airport taxi, Slough to Heathrow taxi, Heathrow to Slough taxi, Windsor to Heathrow taxi, Heathrow transfers, Heathrow minicab, Heathrow private hire"
    });
    setMeta('meta[name="author"]', { name: "author", content: "CheapCabGrab Ltd" });
    setMeta('meta[property="og:title"]', { property: "og:title", content: "Heathrow Airport Taxi | Fixed-Price Transfers | CheapCabGrab" });
    setMeta('meta[property="og:description"]', {
      property: "og:description",
      content: "Instant fixed-price Heathrow taxis to Slough, Windsor, Maidenhead and London. 24/7 licensed drivers, meet & greet, flight tracking and secure online payments."
    });
    setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    setMeta('meta[property="og:url"]', { property: "og:url", content: seoCanonical });
    setMeta('meta[property="og:image"]', { property: "og:image", content: "https://www.cheapcabgrab.com/og-image.jpg" });
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: "Heathrow Airport Taxi | Fixed-Price Transfers | CheapCabGrab" });
    setMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: "Licensed Heathrow taxis, fixed prices and 24/7 airport transfer service. Book online instantly with no surge fees."
    });
    setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: "https://www.cheapcabgrab.com/og-image.jpg" });
    setJsonLd("taxi-service", jsonLd);
    setJsonLd("faq", faqJsonLd);
  };

  applySeo();
  const timers = [window.setTimeout(applySeo, 0), window.setTimeout(applySeo, 750)];
  return () => timers.forEach((timer) => window.clearTimeout(timer));
}, [faqJsonLd, jsonLd, seoCanonical, seoDescription, seoTitle]);

  return (
    <div className="heathrow-page route-page ccg-route-premium ccg-heathrow-premium">
      {/* ✅ SEO Meta + JSON-LD */}


<Helmet>
  {/* Canonical URL */}
  <link rel="canonical" href="https://www.cheapcabgrab.com/heathrow-taxi" />

  {/* SEO Meta */}
  <title>{seoTitle}</title>

  <meta
    name="description"
    content={seoDescription}
  />

  <meta
    name="keywords"
    content="Heathrow taxi, Heathrow airport taxi, Slough to Heathrow taxi, Heathrow to Slough taxi, Windsor to Heathrow taxi, Heathrow transfers, Heathrow minicab, Heathrow private hire"
  />

  <meta name="author" content="CheapCabGrab Ltd" />

  {/* OpenGraph */}
  <meta property="og:title" content="Heathrow Airport Taxi | Fixed-Price Transfers | CheapCabGrab" />
  <meta
    property="og:description"
    content="Instant fixed-price Heathrow taxis to Slough, Windsor, Maidenhead and London. 24/7 licensed drivers, meet & greet, flight tracking and secure online payments."
  />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://www.cheapcabgrab.com/heathrow-taxi" />
  <meta property="og:image" content="https://www.cheapcabgrab.com/og-image.jpg" />

  {/* Twitter Cards */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Heathrow Airport Taxi | Fixed-Price Transfers | CheapCabGrab" />
  <meta
    name="twitter:description"
    content="Licensed Heathrow taxis, fixed prices and 24/7 airport transfer service. Book online instantly with no surge fees."
  />
  <meta name="twitter:image" content="https://www.cheapcabgrab.com/og-image.jpg" />

  {/* JSON-LD (injected from improved jsonLd object) */}
  <script type="application/ld+json" data-heathrow-taxi-jsonld="taxi-service">{JSON.stringify(jsonLd)}</script>

<script type="application/ld+json" data-heathrow-taxi-jsonld="faq">
{JSON.stringify(faqJsonLd)}
</script>



</Helmet>





      <LiveStrip variant="heathrow" />


      <main className="heathrow-main route-main">
        {/* 🟦 HERO */}
        <section className="ccg-route-hero ccg-heathrow-hero">
          <div className="ccg-route-hero-shell">
            <div className="ccg-route-hero-top">
              <div className="ccg-route-hero-copy">
                <span className="ccg-route-eyebrow">Heathrow airport taxi service</span>
                <h1>Heathrow Airport Taxi &amp; Fixed-Price Transfers</h1>
                <p>Terminals 2, 3, 4 &amp; 5 - Slough, Windsor, Maidenhead &amp; London</p>
                <p>
                  Book reliable Heathrow taxis with licensed drivers, flight tracking and meet &amp; greet.
                  Fixed upfront fares with secure online booking - no surge pricing, ever.
                </p>
              </div>

              <div className="ccg-route-trust-cards" aria-label="CheapCabGrab Heathrow taxi trust signals">
                <article className="ccg-route-trust-card">
                  <b>✓</b>
                  <span>
                    <small>Licensed operator</small>
                    <strong>Heathrow specialists</strong>
                    <span>Terminals 2, 3, 4 and 5</span>
                  </span>
                </article>
                <article className="ccg-route-trust-card">
                  <b>G</b>
                  <span>
                    <small>Google reviews</small>
                    <strong>4.8 ★★★★★</strong>
                    <span>Trusted airport transfers</span>
                  </span>
                </article>
              </div>
            </div>

            <div className="ccg-route-quote-wrap" id="heathrow-quote-form">
              <div className="ccg-route-quote-head">
                <span className="ccg-route-quote-label">Get instant Heathrow taxi quotes</span>
                <span className="ccg-route-quote-note"><i /> Free - Fast - No obligation</span>
              </div>
              <PostcodeQuotenew />
              <ul className="ccg-route-quote-promises">
                <li>Fixed upfront fare</li>
                <li>Licensed drivers</li>
                <li>Flight tracking</li>
                <li>Secure online booking</li>
              </ul>
              <PremiumContactStrip />
            </div>

            <ul className="ccg-route-flags">
              <li>Heathrow specialists - terminals 2, 3, 4 and 5</li>
              <li>Fixed, upfront fares - no surge pricing</li>
              <li>Meet &amp; greet, flight tracking, licensed drivers</li>
              <li>Saloon, MPV, 8-16 seat minibus and coach options</li>
            </ul>

            <div className="ccg-route-review-strip">
              <CcgPremiumReviews pageKey="/heathrow-taxi" ariaLabel="Heathrow taxi customer reviews" />
            </div>
          </div>
        </section>

        <CcgRouteLhrStatusCompact title="Live Heathrow checks for airport taxi bookings" />

        <section className="route-section ccg-heathrow-intro">
          <header className="route-section-header">
            <h2>Book Heathrow taxis with live journey context</h2>
            <p>
              Keep the fixed-price Heathrow taxi booking flow, then check roads, rail, flights,
              terminal pressure, queues and weather before you travel.
            </p>
          </header>
          <div className="route-overview-grid">
            <article>
              <h3>Airport pickup coverage</h3>
              <p>Private hire and taxi transfers for Heathrow terminals 2, 3, 4 and 5, nearby hotels, Bath Road and airport business locations.</p>
            </article>
            <article>
              <h3>Live Heathrow status</h3>
              <p>
                Live status helps passengers plan around flight disruption, terminal queues, weather, road pressure and rail issues.
              </p>
            </article>
            <article>
              <h3>Fixed fare booking</h3>
              <p>See your fare before confirming, with vehicle choices for solo travellers, families, business passengers and groups.</p>
            </article>
          </div>
        </section>

        {/* Floating CTA (desktop) */}
        <button className="route-floating-book-btn" onClick={scrollToForm}>
          Book Heathrow Taxi
        </button>

        {/* ℹ️ QUICK HEATHROW INFO + TOOLS */}

{/* ℹ️ QUICK HEATHROW INFO + TOOLS */}
<section className="heathrow-info-section">

  {/* CARD 1 — HEATHROW OVERVIEW */}
  <div className="heathrow-info-card">
    <h2>
      <FaPlaneDeparture /> Heathrow at a glance
    </h2>
    <p>
      Heathrow Airport has four active passenger terminals (2, 3, 4 and 5) with dedicated
      pickup points for private hire and taxi services. We cover all terminals, hotels and
      nearby business parks.
    </p>
    <ul>
      <li>✔ Terminals 2–5 covered</li>
      <li>✔ Hotels in Heathrow perimeter &amp; Bath Road</li>
      <li>✔ Business parks &amp; long-stay car parks</li>
    </ul>
    <a href="/heathrow" className="heathrow-link">
      Full Heathrow guide →
    </a>
  </div>

  {/* CARD 2 — TERMINAL FINDER */}
  <div className="heathrow-info-card">
    <h2>🛫 Heathrow Terminal Finder</h2>
    <p>
      Not sure which terminal your airline uses at Heathrow? Use our terminal finder tool
      to check your airline’s terminal and contact details.
    </p>
    <a href="/terminalfinder" className="heathrow-button">
      Open Terminal Finder →
    </a>
  </div>

  {/* CARD 3 — AIRLINE CONTACTS */}
  <div className="heathrow-info-card">
    <h2>📞 Airline Contacts</h2>
    <p>
      Need to confirm your flight or baggage rules? Quickly look up customer service details
      for airlines operating from Heathrow.
    </p>
    <a href="/airlineContacts" className="heathrow-link">
      View airline contacts →
    </a>
  </div>

  {/* ✅ CARD 4 — FLIGHT CHECKER (NEW & VERY STRONG) */}
  <div className="heathrow-info-card">
    <h2>✈️ Heathrow Flight Checker</h2>
    <p>
      Check live Heathrow arrivals and departures by flight number. View terminal,
      aircraft type, scheduled time and flight status instantly.
    </p>
    <a href="/flight-checker" className="heathrow-button">
      Check your flight →
    </a>
  </div>

  {/* ✅ CARD 5 — TRAIN VS TAXI */}
  <div className="heathrow-info-card">
    <h2>🚆 Train vs Taxi</h2>
    <p>
      Compare real train fares with taxi costs for your Heathrow journey.
      See what’s cheaper, faster and less tiring.
    </p>
    <a href="/train-vs-taxi" className="heathrow-link">
      Compare now →
    </a>
  </div>

  {/* ✅ CARD 6 — PARKING VS TAXI */}
  <div className="heathrow-info-card">
    <h2>🅿️ Parking vs Taxi</h2>
    <p>
      Compare Heathrow parking costs with two-way taxi fares.
      Find out when a taxi is cheaper than airport parking.
    </p>
    <a href="/heathrow-parking-vs-taxi" className="heathrow-link">
      Compare costs →
    </a>
  </div>

</section>




        {/* 🚗 OUR HEATHROW TAXI SERVICES / FLEET */}
        <section className="heathrow-fleet-section">
          <h2>Our Heathrow taxi vehicles</h2>
          <p className="heathrow-section-intro">
            From solo travellers to large groups, we have vehicles to match every Heathrow journey.
            All vehicles are licensed, insured and regularly inspected.
          </p>

          <div className="heathrow-fleet-grid">
            <div className="fleet-card">
              <h3>Saloon</h3>
              <p>Ideal for 1–3 passengers with light luggage travelling between Heathrow and SL1–SL3.</p>
              <ul>
                <li>Up to 3 passengers</li>
                <li>2–3 medium suitcases</li>
                <li>Perfect for business or solo trips</li>
              </ul>
            </div>

            <div className="fleet-card">
              <h3>Estate / MPV</h3>
              <p>Extra luggage space for families and longer stays.</p>
              <ul>
                <li>Up to 4 passengers</li>
                <li>Large boot for extra bags</li>
                <li>Great for Heathrow hotel runs</li>
              </ul>
            </div>

            <div className="fleet-card">
              <h3>8-Seater Minibus</h3>
              <p>Perfect for groups and extended families travelling together.</p>
              <ul>
                <li>Up to 8 passengers</li>
                <li>Plenty of luggage room</li>
                <li>Ideal for events and tours</li>
              </ul>
            </div>

            <div className="fleet-card">
              <h3>14-Seater / Coach</h3>
              <p>For larger groups, corporate events and tour parties.</p>
              <ul>
                <li>14+ passengers</li>
                <li>Great for teams and group travel</li>
                <li>Pre-book only</li>
              </ul>
            </div>
          </div>

          <a href="/fleet" className="heathrow-link">
            View full fleet →
          </a>
        </section>

        {/* 💷 FARES & JOURNEY TIMES */}
        <section className="heathrow-fares-section">
          <h2>Typical Heathrow taxi fares &amp; journey times</h2>
          <p className="heathrow-section-intro">
            These are example guide prices for standard saloon vehicles. Final fare depends on exact
            pickup location, time of day and vehicle type. You’ll always see your fixed price before
            you confirm your booking.
          </p>

          <div className="heathrow-fares-grid">
            <div className="fare-card">
              <h3>Heathrow ↔ Slough (SL1–SL3)</h3>
              <p>
                <FaRoute /> Approx. 9–12 miles
              </p>
              <p>
                <FaClock /> 20–35 minutes
              </p>
              <p>
                💷 From <strong>£35–£55</strong>
              </p>
            </div>

            <div className="fare-card">
              <h3>Heathrow ↔ Windsor</h3>
              <p>
                <FaRoute /> Approx. 8–10 miles
              </p>
              <p>
                <FaClock /> 20–30 minutes
              </p>
              <p>
                💷 From <strong>£40–£60</strong>
              </p>
            </div>

            <div className="fare-card">
              <h3>Heathrow ↔ Maidenhead</h3>
              <p>
                <FaRoute /> Approx. 15–18 miles
              </p>
              <p>
                <FaClock /> 25–40 minutes
              </p>
              <p>
                💷 From <strong>£50–£70</strong>
              </p>
            </div>

            <div className="fare-card">
              <h3>Heathrow ↔ Central London</h3>
              <p>
                <FaRoute /> Approx. 16–22 miles
              </p>
              <p>
                <FaClock /> 40–70 minutes (traffic dependent)
              </p>
              <p>
                💷 From <strong>£70–£110</strong>
              </p>
            </div>
          </div>
        </section>



<div style={{ height: "40px" }}></div>


{/* ============================
    💷 Heathrow Fare Comparison (Static)
    Data captured: 3 Dec 2025 – 20:50
   ============================ */}


<section id="fare-comparison" className="heathrow-section">
  <h2 className="heathrow-section-title">💷 Heathrow Taxi Fare Comparison (SL1 → Heathrow T4)</h2>

  <p className="heathrow-section-intro">
    We compared real quotes on <strong>3 December 2025 at 20:50</strong> for a 
    <strong> standard saloon car (2 passengers, 2 bags)</strong> from 
    <strong> SL1 2LL to Heathrow Terminal 4.</strong>  
    Prices vary by time of day and demand — this table shows how much you can save.
  </p>

  <div className="fare-compare-table">
    <div className="fare-compare-row fare-header">
      <span>Provider</span>
      <span>Price</span>
      <span>Notes</span>
    </div>

    <div className="fare-compare-row">
      <span>🌐 AirportTaxis-UK</span>
      <span><strong>£41.00</strong></span>
      <span>Fixed price</span>
    </div>

    <div className="fare-compare-row">
      <span>🚕 Bolt (via Minicabit)</span>
      <span><strong>£54.70</strong></span>
      <span>Live pricing</span>
    </div>

    <div className="fare-compare-row">
      <span>🚗 Addison Lee</span>
      <span><strong>£122.07</strong></span>
      <span>Premium service</span>
    </div>

    <div className="fare-compare-row">
      <span>🏢 Heathrow Official Taxi</span>
      <span><strong>£70.99</strong></span>
      <span>Official LHR provider</span>
    </div>

    <div className="fare-compare-row">
      <span>🚖 1st Airport Taxis</span>
      <span><strong>£57.00</strong></span>
      <span>Fixed price</span>
    </div>

    <div className="fare-compare-row fare-highlight">
      <span>⭐ CheapCabGrab</span>
      <span><strong>£40.44</strong></span>
      <span>Fixed price – lowest</span>
    </div>
  </div>

  <p className="fare-compare-disclaimer">
    * Prices were captured manually on <strong>3 Dec 2025</strong>. Competitor prices may change 
    based on traffic, availability, and surge conditions.  
    CheapCabGrab provides <strong>fixed-price fares with no surge pricing</strong>.
  </p>
</section>




        {/* ❓ HEATHROW-SPECIFIC FAQ */}
        <section className="heathrow-faq-section">
          <h2>Heathrow taxi FAQs</h2>

          <div className="heathrow-faq-grid">
            <div className="faq-item">
              <h3>Where do I meet my driver at Heathrow?</h3>
              <p>
                Your driver will usually meet you in the arrivals hall at your terminal with a
                nameboard. If you prefer kerbside pickup, we’ll confirm the exact meeting point in
                your booking confirmation.
              </p>
            </div>

            <div className="faq-item">
              <h3>Do you track my flight?</h3>
              <p>
                Yes, for most airlines we track your flight number and adjust pickup time for delays
                where possible. Standard free waiting time is included; we’ll explain this clearly
                when you book.
              </p>
            </div>

            <div className="faq-item">
              <h3>Are parking and drop-off charges included?</h3>
              <p>
                Heathrow’s terminal drop-off and parking fees may be added to your fare. We’ll show
                you a clear breakdown before you confirm so there are no surprises.
              </p>
            </div>

            <div className="faq-item">
              <h3>Can I request a child seat?</h3>
              <p>
                Yes, child seats can be requested in advance for most Heathrow journeys. Please add
                details in the booking notes so we can allocate the right vehicle.
              </p>
            </div>
          </div>

          <a href="/faq" className="heathrow-link">
            See all FAQs →
          </a>
        </section>



{/* ============================
    🏰 Nearby Attractions Section
   ============================ */}
<section className="heathrow-info-section">
  <h2 className="heathrow-section-title">🏰 Attractions Near Heathrow Airport</h2>

  <p className="heathrow-section-intro">
    Heathrow is surrounded by world-famous attractions ideal for layovers,
    weekend breaks and family days out. Here are the most popular places
    within 15–25 minutes of Heathrow:
  </p>

  <div className="heathrow-info-grid">

    <div className="heathrow-info-card">
      <h3>Windsor Castle</h3>
      <p>One of the official residences of His Majesty The King.</p>
      <ul>
        <li>📍 <b>7 miles</b> from Heathrow</li>
        <li>⏱ <b>15–20 mins</b></li>
        <li>💷 Typical fare: <b>£35–£50</b></li>
      </ul>
    </div>

    <div className="heathrow-info-card">
      <h3>Legoland Windsor Resort</h3>
      <p>Top family attraction with over 55 rides and shows.</p>
      <ul>
        <li>📍 <b>12 miles</b> from Heathrow</li>
        <li>⏱ <b>20–30 mins</b></li>
        <li>💷 Typical fare: <b>£40–£55</b></li>
      </ul>
    </div>

    <div className="heathrow-info-card">
      <h3>Kew Gardens</h3>
      <p>UNESCO World Heritage botanical gardens.</p>
      <ul>
        <li>📍 <b>9 miles</b> from Heathrow</li>
        <li>⏱ <b>20–25 mins</b></li>
        <li>💷 Typical fare: <b>£30–£45</b></li>
      </ul>
    </div>

    <div className="heathrow-info-card">
      <h3>Richmond Park</h3>
      <p>London’s largest royal park — famous for free-roaming deer.</p>
      <ul>
        <li>📍 <b>10 miles</b> from Heathrow</li>
        <li>⏱ <b>20–30 mins</b></li>
        <li>💷 Typical fare: <b>£35–£50</b></li>
      </ul>
    </div>

    <div className="heathrow-info-card">
      <h3>Thorpe Park</h3>
      <p>UK’s top thrill-ride theme park.</p>
      <ul>
        <li>📍 <b>11 miles</b> from Heathrow</li>
        <li>⏱ <b>20–30 mins</b></li>
        <li>💷 Typical fare: <b>£40–£55</b></li>
      </ul>
    </div>

    <div className="heathrow-info-card">
      <h3>Hampton Court Palace</h3>
      <p>Historic Tudor palace overlooking the River Thames.</p>
      <ul>
        <li>📍 <b>14 miles</b> from Heathrow</li>
        <li>⏱ <b>25–35 mins</b></li>
        <li>💷 Typical fare: <b>£40–£60</b></li>
      </ul>
    </div>

  </div>

  <a href="/heathrow-attractions" className="heathrow-section-link">
    View full Heathrow attractions guide →
  </a>
</section>






{/* ============================
    🏨 Hotels Near Heathrow
   ============================ */}
<section className="heathrow-info-section">
  <h2 className="heathrow-section-title">🏨 Hotels Near Heathrow Airport</h2>

  <p className="heathrow-section-intro">
    Whether you have an early flight or need somewhere comfortable after landing,
    these hotels near Heathrow offer excellent transport links and reliable service.
  </p>

  <div className="heathrow-info-grid">

    <div className="heathrow-info-card">
      <h3>Sofitel Heathrow – Terminal 5</h3>
      <p>Luxury 5★ hotel connected directly to Terminal 5.</p>
      <ul>
        <li>⭐ 4.6/5 average rating</li>
        <li>🚶 Walk to T5 departures</li>
        <li>💷 From £150–£260/night</li>
      </ul>
    </div>

    <div className="heathrow-info-card">
      <h3>Hilton Garden Inn – Terminals 2 & 3</h3>
      <p>Modern 4★ hotel with runway views.</p>
      <ul>
        <li>⭐ 4.4/5 average rating</li>
        <li>🚶 Short walk to T2/T3</li>
        <li>💷 From £120–£200/night</li>
      </ul>
    </div>

    <div className="heathrow-info-card">
      <h3>Premier Inn Heathrow (Bath Road)</h3>
      <p>Reliable budget-friendly family hotel.</p>
      <ul>
        <li>⭐ 4.3/5 average rating</li>
        <li>🚗 5–10 mins from terminals</li>
        <li>💷 From £60–£110/night</li>
      </ul>
    </div>

    <div className="heathrow-info-card">
      <h3>Novotel Heathrow T1/T2/T3</h3>
      <p>Spacious rooms and good value for travellers.</p>
      <ul>
        <li>⭐ 4.2/5 average rating</li>
        <li>🚗 10 mins from terminals</li>
        <li>💷 From £75–£140/night</li>
      </ul>
    </div>

    <div className="heathrow-info-card">
      <h3>Holiday Inn Heathrow M4 J4</h3>
      <p>Good for stopovers and business travel.</p>
      <ul>
        <li>⭐ 4.1/5 average rating</li>
        <li>🚗 10–12 mins from terminals</li>
        <li>💷 From £70–£130/night</li>
      </ul>
    </div>

  </div>

  <a href="/heathrow-hotels" className="heathrow-section-link">
    View full Heathrow hotels guide →
  </a>
</section>





        {/* 📞 SUPPORT / CONTACT */}
        <section className="heathrow-contact-section">
          <div className="heathrow-contact-card">
            <h2>
              <FaPhoneAlt /> Need help with a Heathrow booking?
            </h2>
            <p>
              If you have any questions about pickups, flight delays or special requirements at
              Heathrow, our support team is here to help.
            </p>
            <a href="/contact" className="heathrow-button">
              Contact our support team →
            </a>
          </div>
        </section>

        {/* Floating book-now button (optional) */}
        <div className="route-bottom-cta">
          <button className="route-bottom-cta-btn" onClick={scrollToForm}>
            Book Heathrow Taxi
          </button>
        </div>
      </main>

      <FloatingSupportChat
        brand="ccg"
        defaultFlow="enquiry"
        position="right"
        showAfterMs={9000}
        nudgeText="Need Heathrow taxi help?"
      />
    </div>
  );
};

export default HeathrowTaxi;
